import { ChevronDown } from "lucide-react";
import * as React from "react";

import type { OrgLlmConnectionRecord, OrgLlmModel } from "@/shared/domain/orgs";
import { useUpdateOrgLlmProvider } from "@/shared/domain/orgs/hooks";
import {
  ConfirmDialog,
  DropdownMenu,
  RobotoButton,
} from "@/shared/foundation/primitives";

import { AddRobotoModel } from "./AddRobotoModel";
import {
  modelListRequest,
  modelListSaveErrorMessage,
  offeredInPicker,
  withModelEnabled,
  withoutModel,
} from "./modelList";
import { ModelRowActions } from "./ModelRowActions";
import { ModelTable } from "./ModelTable";
import styles from "./OrgAiModels.module.css";
import { OwnModelDialog } from "./OwnModelDialog";
import type { RobotoModelChoice } from "./robotoModelChoices";

interface ModelListEditorProps {
  orgId: string;
  /** The saved list. */
  models: readonly OrgLlmModel[];
  /** The saved list's `modified`, sent with each save. */
  listModified: string;
  connections: readonly OrgLlmConnectionRecord[];
  isPremium: boolean;
  /**
   * Whether the org may use its own LLM connections (a feature flag). Off,
   * own models already on the list can only be switched or removed, and the
   * utility role is neither shown nor offered.
   */
  ownConnectionsEnabled: boolean;
  robotoModelIds?: ReadonlyMap<string, string>;
  robotoUtilityModelId?: string;
  /** The Roboto-managed models that may be added, including ones already on the list. */
  robotoChoices: readonly RobotoModelChoice[];
}

/**
 * The model table as an admin sees it: each row's Enabled switch and
 * actions, an "Add model" button, the own-model dialog and the remove
 * confirmation. Every change is saved at once as the whole list.
 */
export function ModelListEditor({
  orgId,
  models,
  listModified,
  connections,
  isPremium,
  ownConnectionsEnabled,
  robotoModelIds,
  robotoUtilityModelId,
  robotoChoices,
}: ModelListEditorProps) {
  const updateMutation = useUpdateOrgLlmProvider();
  const [addingRoboto, setAddingRoboto] = React.useState(false);
  // The own model in the dialog: `"new"` to add one, `null` when closed.
  const [ownDialog, setOwnDialog] = React.useState<OrgLlmModel | "new" | null>(
    null,
  );
  const [removing, setRemoving] = React.useState<OrgLlmModel | null>(null);
  // The id of the model whose Enabled switch started the save in flight.
  const [toggling, setToggling] = React.useState<string>();

  const save = (
    next: OrgLlmModel[],
    callbacks?: { onSuccess?: () => void; onSettled?: () => void },
  ) =>
    updateMutation.mutate(
      { orgId, request: modelListRequest(next, listModified) },
      callbacks,
    );

  const addableRoboto = robotoChoices.filter(
    (c) => !models.some((m) => m.id === c.model.id),
  );
  // A choice shown with an unavailable reason cannot be picked.
  const canAddRoboto = addableRoboto.some(
    (c) => c.unavailableReason === undefined,
  );
  // Offering own models needs both the flag and the premium tier.
  const offersOwn = isPremium && ownConnectionsEnabled;
  const canAddOwn = offersOwn && connections.length > 0;
  const addRoboto = () => {
    updateMutation.reset();
    setAddingRoboto(true);
  };
  const addOwn = () => setOwnDialog("new");
  const needsConnection = offersOwn && connections.length === 0;

  return (
    <>
      {updateMutation.isError && removing === null && (
        <p className={styles.error} role="alert">
          {modelListSaveErrorMessage(updateMutation.error)}
        </p>
      )}
      <ModelTable
        models={models}
        connections={connections}
        isPremium={isPremium}
        ownConnectionsEnabled={ownConnectionsEnabled}
        robotoModelIds={robotoModelIds}
        robotoUtilityModelId={robotoUtilityModelId}
        enabledControl={{
          busy: updateMutation.isPending,
          pendingId: updateMutation.isPending ? toggling : undefined,
          onToggle: (model, enabled) => {
            setToggling(model.id);
            // Cleared once settled, so a later save started elsewhere does not
            // show this switch's spinner.
            save(withModelEnabled(models, model.id, enabled), {
              onSettled: () => setToggling(undefined),
            });
          },
        }}
        renderActions={(model) => (
          <ModelRowActions
            orgId={orgId}
            model={model}
            models={models}
            canVerify={offersOwn}
            canEditOwn={ownConnectionsEnabled}
            offersUtility={ownConnectionsEnabled}
            busy={updateMutation.isPending}
            onEdit={setOwnDialog}
            onSave={(next) => save(next)}
            onRemove={(m) => {
              updateMutation.reset();
              setRemoving(m);
            }}
          />
        )}
      />

      {addingRoboto ? (
        <AddRobotoModel
          choices={addableRoboto}
          pending={updateMutation.isPending}
          onAdd={(model) =>
            save(
              [
                ...models,
                { ...model, is_default: !models.some((m) => m.is_default) },
              ],
              { onSuccess: () => setAddingRoboto(false) },
            )
          }
          onCancel={() => setAddingRoboto(false)}
        />
      ) : (
        (canAddRoboto || canAddOwn || needsConnection) && (
          <div className={styles.toolbar}>
            <AddModelButton
              onAddRoboto={canAddRoboto ? addRoboto : undefined}
              onAddOwn={canAddOwn ? addOwn : undefined}
            />
            {needsConnection && (
              <p className={styles.note}>
                To add your own models, first add a connection under LLM
                connections.
              </p>
            )}
          </div>
        )
      )}

      <OwnModelDialog
        orgId={orgId}
        open={ownDialog !== null}
        onOpenChange={(open) => !open && setOwnDialog(null)}
        models={models}
        listModified={listModified}
        connections={connections}
        model={
          ownDialog === "new" || ownDialog === null ? undefined : ownDialog
        }
      />
      <RemoveModelConfirm
        model={removing}
        models={models}
        pending={updateMutation.isPending}
        error={updateMutation.error}
        onCancel={() => setRemoving(null)}
        onConfirm={(next) => save(next, { onSuccess: () => setRemoving(null) })}
        showUtility={ownConnectionsEnabled}
      />
    </>
  );
}

/**
 * "Add model", for the kinds of model that can be added (each handler given
 * only when its kind can): a menu when both can, a button straight to the one
 * that can, nothing when neither can.
 */
function AddModelButton({
  onAddRoboto,
  onAddOwn,
}: {
  onAddRoboto: (() => void) | undefined;
  onAddOwn: (() => void) | undefined;
}) {
  if (onAddRoboto !== undefined && onAddOwn !== undefined) {
    return (
      <DropdownMenu>
        <DropdownMenu.Trigger
          render={
            <RobotoButton variant="secondary" size="sm">
              Add model
              <ChevronDown aria-hidden className={styles.triggerIcon} />
            </RobotoButton>
          }
        />
        <DropdownMenu.Content>
          <DropdownMenu.Item onClick={onAddRoboto}>
            Roboto model
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={onAddOwn}>
            Model on a connection
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu>
    );
  }
  const onAdd = onAddRoboto ?? onAddOwn;
  if (onAdd === undefined) {
    return null;
  }
  return (
    <RobotoButton variant="secondary" size="sm" onClick={onAdd}>
      Add model
    </RobotoButton>
  );
}

function RemoveModelConfirm({
  model,
  models,
  pending,
  error,
  onCancel,
  onConfirm,
  showUtility,
}: {
  model: OrgLlmModel | null;
  models: readonly OrgLlmModel[];
  pending: boolean;
  error: Error | null;
  onCancel: () => void;
  onConfirm: (next: OrgLlmModel[]) => void;
  /** Whether the utility role is shown (the own-connections flag). */
  showUtility: boolean;
}) {
  const next = model === null ? [] : withoutModel(models, model.id);
  return (
    <ConfirmDialog
      open={model !== null}
      onOpenChange={(open) => !open && onCancel()}
      title={`Remove ${model?.label ?? "model"}?`}
      description={
        model === null ? undefined : removalEffect(model, next, showUtility)
      }
      confirmLabel="Remove"
      destructive
      pending={pending}
      onConfirm={() => onConfirm(next)}
    >
      {error !== null && (
        <p className={styles.error} role="alert">
          {modelListSaveErrorMessage(error)}
        </p>
      )}
    </ConfirmDialog>
  );
}

/** What removing `model` does, given the list it leaves; `undefined` when members notice nothing. */
function removalEffect(
  model: OrgLlmModel,
  next: readonly OrgLlmModel[],
  showUtility: boolean,
): string | undefined {
  const parts: string[] = [];
  if (offeredInPicker(model)) {
    parts.push("Members can no longer pick it in chat.");
  }
  const successor = next.find((m) => m.is_default);
  if (model.is_default && successor !== undefined) {
    parts.push(`New chats start on ${successor.label}.`);
  }
  if (showUtility && model.is_utility === true) {
    parts.push(
      "Titles, compression and log scans move to Roboto's utility model.",
    );
  }
  return parts.length === 0 ? undefined : parts.join(" ");
}
